import { FormattedMessage, useIntl } from 'react-intl';
import { Fade, Stack } from '@mui/material';
import { FilterMultiSelect, SearchField, renderCheckboxOption } from '@atoms/inputs';
import { Box, Flex } from '@atoms/layout';
import { Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { ActiveFilters, type FiltersDisplayCollection } from '@molecules/filters';

import { RECURRENCE_TYPES_LIST } from '@libs/models/shiftTemplates';
import { useShiftTemplatesFiltersStore } from '@libs/store/shiftTemplates/shiftTemplatesListFiltersStore';

type FiltersMenuProps = {
  loading: boolean;
  localizationSubset: string;
};
export function ShiftTemplatesFilters({ loading, localizationSubset }: FiltersMenuProps) {
  const intl = useIntl();
  const { activeFilters, clearFilter, clearFilters, getFilter, setFilterFromOptions, setFilter } = useShiftTemplatesFiltersStore();
  const shouldShowClearAllButton =
    Object.keys(activeFilters).filter((filterKey) => filterKey != 'location').length > 0;

  const recurrencetypeFilterOptions =
    RECURRENCE_TYPES_LIST.map((reqType) => ({
      key: reqType,
      label: intl.formatMessage({
        id: `${localizationSubset}.filters.recurrencetype.items.${reqType.toLowerCase()}`,
      }),
      value: reqType,
    })) || [];

  const timetypeFilterOptions = ['Day', 'Eve', 'Noc'].map((type) => ({
    key: type,
    label: intl.formatMessage({ id: `${localizationSubset}.filters.timetype.items.${type.toLowerCase()}` }),
    value: type,
  }));

  const filtersDisplayCollection: FiltersDisplayCollection = Object.keys(
    activeFilters,
  ).reduce<FiltersDisplayCollection>((acc, filterKey) => {
    return ['location'].includes(filterKey)
      ? acc
      : {
          ...acc,
          [filterKey]: {
            ...activeFilters[filterKey],
            label: <FormattedMessage id={`${localizationSubset}.filters.${filterKey}.label`} />,
          },
        };
  }, {});

  return (
    <Stack direction={'column'} spacing={1}>
      <Flex justifyContent="space-between" gap={2}>
        <Flex gap={2} flexWrap="wrap">
          <Box width={220}>
            <FilterMultiSelect
              size="small"
              disabled={loading}
              options={recurrencetypeFilterOptions}
              inlineLabel
              label={`${intl.formatMessage({ id: `${localizationSubset}.filters.recurrencetype.label` })}:`}
              onApply={(v) => setFilterFromOptions('recurrencetype', v, recurrencetypeFilterOptions)}
              onClear={() => clearFilter('recurrencetype')}
              value={getFilter<string[]>('recurrencetype')?.value ?? []}
              fullWidth
              renderOption={renderCheckboxOption}
            />
          </Box>
          <Box width={220}>
            <FilterMultiSelect
              size="small"
              disabled={loading}
              options={timetypeFilterOptions}
              inlineLabel
              label={`${intl.formatMessage({ id: `${localizationSubset}.filters.timetype.label` })}:`}
              onApply={(v) => setFilterFromOptions('timetype', v, timetypeFilterOptions)}
              onClear={() => clearFilter('timetype')}
              value={getFilter<string[]>('timetype')?.value ?? []}
              fullWidth
              renderOption={renderCheckboxOption}
            />
          </Box>
        </Flex>
        <SearchField
          sx={{ width: 260, flexShrink: 0 }}
          value={getFilter<string>('search')?.value ?? ''}
          onChange={(elem: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            elem.target.value ? setFilter({ key: 'search', value: elem.target.value }) : clearFilter('search')
          }
        />
      </Flex>
      <Stack direction={'row'} spacing={2}>
        <ActiveFilters filters={filtersDisplayCollection} onDelete={(item) => clearFilter(item.key)} />
        <Box flex={1} />
        {shouldShowClearAllButton && (
          <Fade in={shouldShowClearAllButton} timeout={150}>
            <Button
              variant="passive"
              onClick={() => clearFilters()}
              startIcon={<FaIcon name="xmark" />}
              sx={{ ml: 'auto', height: '32px' }}
            >
              <FormattedMessage id="common.clearAllFilters" />
            </Button>
          </Fade>
        )}
      </Stack>
    </Stack>
  );
}
